import { useMemo } from 'react';
import { format } from 'date-fns';

/** A 'yyyy-MM-dd' day widened to a full-day ISO instant for the backend. */
export function dayStartIso(day: string | null): string | undefined {
  if (!day) return undefined;
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}
export function dayEndIso(day: string | null): string | undefined {
  if (!day) return undefined;
  const d = new Date(`${day}T23:59:59`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * Resolve the effective date range from URL params, defaulting to the last 30 days when none is
 * set. "All time" is encoded explicitly as `?range=all` so it survives reloads — and so a fresh
 * navigation with no params always falls back to the 30-day default instead of all-time (this is
 * what makes clicking the already-active nav item land on a sensibly-filtered page).
 */
export function useDateRange(searchParams: URLSearchParams) {
  const rangeAll = searchParams.get('range') === 'all';
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  return useMemo(() => {
    let fromDay: string | null;
    let toDay: string | null;
    if (rangeAll) {
      fromDay = null;
      toDay = null;
    } else if (fromParam || toParam) {
      fromDay = fromParam;
      toDay = toParam;
    } else {
      const today = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29);
      fromDay = format(start, 'yyyy-MM-dd');
      toDay = format(today, 'yyyy-MM-dd');
    }
    return { fromDay, toDay, fromIso: dayStartIso(fromDay), toIso: dayEndIso(toDay) };
  }, [rangeAll, fromParam, toParam]);
}

/** Write a (from,to) selection into params; the all-time choice becomes `?range=all`. */
export function applyDateRange(params: URLSearchParams, from: string | null, to: string | null) {
  params.delete('range');
  if (from) params.set('from', from);
  else params.delete('from');
  if (to) params.set('to', to);
  else params.delete('to');
  if (!from && !to) params.set('range', 'all');
}
